import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Music2, Heart, Calendar, Mic, Sparkles, Quote, ChevronDown, MessageCircle, Phone, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Navbar } from "@/components/landing/Navbar";
import { AmaliLogo } from "@/components/landing/Logo";
import { ContactSection, SectionLabel } from "@/components/landing/ContactSection";
import { CountUp } from "@/components/landing/CountUp";
import { FloatingCTA } from "@/components/landing/FloatingCTA";
import { useT } from "@/lib/i18n";

const HERO_BG = "https://customer-assets.emergentagent.com/job_elegance-sings/artifacts/t5az8jpk_IMG_9771.jpeg";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_elegance-sings/artifacts/wn84sn5e_IMG_9773.jpeg";

const GALLERY = [
  { url: "https://customer-assets.emergentagent.com/job_elegance-sings/artifacts/t5az8jpk_IMG_9771.jpeg", h: "h-[28rem]", alt: "Valeria Jacek na pozornici — Moj glas" },
  { url: "https://customer-assets.emergentagent.com/job_elegance-sings/artifacts/mo294r92_IMG_9772.jpeg", h: "h-80", alt: "Valeria Jacek u Voice studiju" },
  { url: "https://images.pexels.com/photos/29040997/pexels-photo-29040997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", h: "h-72", alt: "Elegantna postavka stola za vjenčanje" },
  { url: "https://customer-assets.emergentagent.com/job_elegance-sings/artifacts/wn84sn5e_IMG_9773.jpeg", h: "h-96", alt: "Vokalni nastup uz mikrofon" },
  { url: "https://customer-assets.emergentagent.com/job_elegance-sings/artifacts/nmm4hhus_IMG_9774.jpeg", h: "h-80", alt: "Pozornica pod ružičastim reflektorima" },
  { url: "https://images.pexels.com/photos/12954015/pexels-photo-12954015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", h: "h-72", alt: "Vanjski wedding reception" },
];

const SERVICE_ICONS = [Heart, Sparkles, Mic];

const LANG_KEY = "amali_lang";

const Landing = () => {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || "hr");
  const t = useT(lang);

  const onSetLang = (v) => {
    setLang(v);
    localStorage.setItem(LANG_KEY, v);
  };

  const goContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-navy text-cream relative overflow-x-clip" data-testid="landing-root">
      <Navbar lang={lang} setLang={onSetLang} t={t} />

      {/* HERO — editorial split layout */}
      <section id="hero" className="relative min-h-[100svh] pt-28 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        {/* Layered backdrop: navy + ultra-faded photo + radial gold glow + grain */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-navy" />
          <img
            src={HERO_BG}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-[0.10] kenburns"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/90 via-navy/80 to-navy" />
          <div
            className="absolute -top-32 -right-32 w-[680px] h-[680px] rounded-full opacity-40"
            style={{
              background:
                "radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 60%)",
            }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full opacity-30"
            style={{
              background:
                "radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 60%)",
            }}
          />
          <div className="absolute inset-0 grain-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[calc(100svh-9rem)]">
          {/* LEFT — editorial text column */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="flex items-center gap-4 mb-6 lg:mb-8"
            >
              <span className="w-12 h-px bg-gold/70" />
              <span className="text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-gold font-medium whitespace-nowrap">
                {t.hero.eyebrow}
              </span>
            </motion.div>

            <h1 className="font-serif font-light leading-[0.92] tracking-tight">
              <span
                className="block text-[22vw] sm:text-[16vw] lg:text-[11rem] leading-[0.85] gold-text-gradient"
                aria-label={t.hero.title_a}
              >
                {Array.from(t.hero.title_a).map((char, i) => (
                  <motion.span
                    key={i}
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      duration: 0.7,
                      delay: 0.15 + i * 0.08,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="inline-block"
                  >
                    {char}
                  </motion.span>
                ))}
              </span>
              <motion.span
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="block text-cream text-2xl sm:text-4xl lg:text-5xl mt-4 italic font-light"
              >
                {t.hero.title_b}
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="text-cream/75 text-base sm:text-lg max-w-xl mt-7 leading-relaxed font-light"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mt-9"
            >
              <button
                onClick={goContact}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 bg-gold text-navy px-7 sm:px-8 py-4 font-medium tracking-[0.18em] uppercase text-xs sm:text-sm hover:bg-gold-soft transition-all duration-300 shadow-[0_10px_30px_-12px_rgba(212,175,55,0.6)]"
              >
                {t.hero.cta_primary}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="https://wa.me/385993509595"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="group inline-flex items-center justify-center gap-3 border border-gold/60 text-gold px-7 sm:px-8 py-4 font-medium tracking-[0.18em] uppercase text-xs sm:text-sm hover:bg-gold/10 transition-all duration-300"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            </motion.div>

            {/* Featured / credibility bar — hidden on mobile, shown on tablet+ */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="hidden md:flex mt-12 flex-wrap items-center gap-x-6 gap-y-3"
              data-testid="hero-featured-bar"
            >
              <span className="text-[10px] uppercase tracking-[0.35em] text-gold/80">
                {t.hero.featured_label}
              </span>
              <span className="hidden sm:block w-8 h-px bg-gold/40" />
              {t.hero.featured.map((f, i) => (
                <span
                  key={i}
                  className="text-[10px] uppercase tracking-[0.25em] text-cream/70 flex items-center gap-2"
                >
                  <Star size={10} className="text-gold/70 fill-gold/70" />
                  {f}
                </span>
              ))}
            </motion.div>
          </div>

          {/* RIGHT — framed portrait card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: "easeOut" }}
            className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end"
            data-testid="hero-portrait-card"
          >
            <div className="relative w-[78%] max-w-[320px] sm:max-w-[360px] lg:w-full lg:max-w-none aspect-[3/4] group">
              {/* Outer gold frame */}
              <div className="absolute -inset-3 border border-gold/30 pointer-events-none" />
              {/* Inner image */}
              <div className="relative w-full h-full overflow-hidden border border-gold/50 bg-navy-surface">
                <img
                  src={HERO_BG}
                  alt="Valeria Jacek"
                  className="w-full h-full object-cover kenburns"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" />

                {/* Gold corner ornaments */}
                <span className="absolute top-3 left-3 w-7 h-7 border-t-2 border-l-2 border-gold/80" />
                <span className="absolute top-3 right-3 w-7 h-7 border-t-2 border-r-2 border-gold/80" />
                <span className="absolute bottom-3 left-3 w-7 h-7 border-b-2 border-l-2 border-gold/80" />
                <span className="absolute bottom-3 right-3 w-7 h-7 border-b-2 border-r-2 border-gold/80" />

                {/* Vertical side caption */}
                <div className="absolute left-5 top-1/2 -translate-y-1/2 -rotate-90 origin-left whitespace-nowrap">
                  <span className="text-[9px] uppercase tracking-[0.5em] text-gold/70">
                    Live · Vocals · Croatia
                  </span>
                </div>

                {/* Name plate */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-gold/80 mb-2">
                    {t.hero.eyebrow.split("·")[0].trim()}
                  </div>
                  <div className="font-serif text-2xl sm:text-3xl text-cream leading-tight">
                    Valeria Jacek
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-cream/60 mt-1">
                    Vocalist · Đakovo
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* scroll cue — desktop only to avoid mobile clutter */}
        <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/70 flex-col items-center gap-2 animate-float-soft">
          <span className="text-[10px] uppercase tracking-[0.35em]">{t.hero.scroll}</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* Mobile-only credibility marquee right under hero */}
      <section className="md:hidden border-y border-gold/15 bg-navy-deep overflow-hidden" data-testid="mobile-credibility-bar">
        <div className="marquee-track gap-8 py-4">
          {[...t.hero.featured, ...t.hero.featured, ...t.hero.featured].map((f, i) => (
            <span
              key={i}
              className="shrink-0 text-[10px] uppercase tracking-[0.3em] text-cream/70 flex items-center gap-2"
            >
              <Star size={10} className="text-gold/70 fill-gold/70" />
              {f}
            </span>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="section-pad relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[3/4] overflow-hidden border border-gold/25">
              <img src={ABOUT_IMG} alt="Valeria Jacek" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block border border-gold/40 bg-navy-surface px-6 py-5 backdrop-blur-md">
              <div className="text-3xl font-serif gold-text-gradient">Valeria Jacek</div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-cream-muted mt-1">
                Vocalist · Đakovo
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <SectionLabel eyebrow={t.about.eyebrow} />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-cream leading-[1.05]">
              {t.about.heading}
            </h2>
            <p className="text-cream/75 text-base sm:text-lg mt-8 leading-relaxed max-w-2xl font-light">
              {t.about.paragraph}
            </p>
            <p className="font-serif italic text-gold mt-6 text-xl">{t.about.signature}</p>

            <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-12 max-w-xl">
              {t.about.stats.map((s, i) => (
                <div key={i} className="border-t border-gold/30 pt-5" data-testid={`about-stat-${i}`}>
                  <div className="font-serif text-4xl sm:text-5xl gold-text-gradient">
                    <CountUp value={s.value} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-cream-muted mt-2">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="section-pad relative bg-navy-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <SectionLabel eyebrow={t.services.eyebrow} />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-cream leading-[1.05]">
              {t.services.heading}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-16">
            {t.services.items.map((srv, i) => {
              const Icon = SERVICE_ICONS[i] || Music2;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="group relative bg-navy-surface border border-gold/20 p-8 lg:p-10 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_20px_60px_-20px_rgba(212,175,55,0.25)] transition-all duration-500"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-colors duration-500">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-serif text-2xl text-cream mt-6 leading-tight">{srv.title}</h3>
                  <p className="text-cream/70 mt-3 leading-relaxed font-light text-[15px]">
                    {srv.desc}
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {srv.features.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-3 text-sm text-cream/80">
                        <span className="w-1 h-1 bg-gold rounded-full" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="absolute top-6 right-6 font-serif text-5xl text-gold/15 group-hover:text-gold/30 transition-colors">
                    0{i + 1}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REPERTOIRE */}
      <section id="repertoire" className="section-pad relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <SectionLabel eyebrow={t.repertoire.eyebrow} />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-cream leading-[1.05]">
              {t.repertoire.heading}
            </h2>
            <p className="text-cream/70 mt-6 max-w-2xl text-base sm:text-lg font-light leading-relaxed">
              {t.repertoire.desc}
            </p>
          </div>
        </div>

        <div className="mt-16 overflow-hidden relative" data-testid="repertoire-marquee">
          <div className="marquee-track gap-6 px-6">
            {[...t.repertoire.tags, ...t.repertoire.tags].map((tag, i) => (
              <div
                key={i}
                className="shrink-0 border border-gold/30 px-7 py-4 text-cream/85 font-serif text-xl italic tracking-wide hover:border-gold hover:text-gold transition-colors"
              >
                {tag}
              </div>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-navy to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-navy to-transparent" />
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="section-pad bg-navy-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <SectionLabel eyebrow={t.gallery.eyebrow} />
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-cream leading-[1.05]">
                {t.gallery.heading}
              </h2>
              <p className="text-cream/70 mt-6 font-light">{t.gallery.desc}</p>
            </div>
          </div>

          <div className="gallery-grid">
            {GALLERY.map((g, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className={`group relative overflow-hidden border border-gold/15 ${g.h}`}
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.url}
                  alt={g.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="section-pad relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <SectionLabel eyebrow={t.testimonials.eyebrow} />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-cream leading-[1.05]">
              {t.testimonials.heading}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-16">
            {t.testimonials.items.map((tst, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="relative bg-navy-surface border border-gold/20 p-8 hover:border-gold/50 transition-colors"
                data-testid={`testimonial-${i}`}
              >
                <Quote size={28} className="text-gold/40" />
                <p className="font-serif italic text-cream/90 text-lg leading-relaxed mt-5">
                  "{tst.quote}"
                </p>
                <div className="mt-7 pt-5 border-t border-gold/20">
                  <div className="text-gold font-medium tracking-wide">{tst.author}</div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-cream-muted mt-1">
                    {tst.role}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section-pad bg-navy-deep">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <SectionLabel eyebrow={t.faq.eyebrow} />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-cream leading-[1.05]">
              {t.faq.heading}
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {t.faq.items.map((it, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-gold/20 data-[state=open]:border-gold/50"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="text-left text-cream hover:text-gold hover:no-underline py-6 font-serif text-xl sm:text-2xl font-light [&[data-state=open]>svg]:text-gold"
                  data-testid={`faq-trigger-${i}`}
                >
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="text-cream/75 text-base font-light leading-relaxed pb-6 pr-6">
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Banner before Contact */}
      <section className="relative py-20 bg-navy overflow-hidden" data-testid="cta-banner">
        <div className="absolute inset-0 grain-overlay opacity-50" />
        <div className="absolute -left-20 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gold/5 blur-3xl" />
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gold/5 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="border border-gold/30 bg-navy-surface/60 backdrop-blur-sm p-10 md:p-14 lg:p-16 grid lg:grid-cols-5 gap-8 lg:gap-12 items-center"
          >
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-4">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.35em] text-gold font-medium">
                  {t.cta_banner.eyebrow}
                </span>
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-cream leading-[1.1]">
                {t.cta_banner.heading}
              </h3>
              <p className="text-cream/70 mt-5 font-light max-w-xl">{t.cta_banner.desc}</p>
            </div>
            <div className="lg:col-span-2 flex flex-col sm:flex-row lg:flex-col gap-3">
              <button
                onClick={goContact}
                data-testid="cta-banner-primary"
                className="group flex-1 inline-flex items-center justify-center gap-3 bg-gold text-navy px-7 py-4 font-medium tracking-[0.2em] uppercase text-sm hover:bg-gold-soft transition-all duration-300"
              >
                {t.cta_banner.primary}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="https://wa.me/385993509595"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-banner-whatsapp"
                className="group flex-1 inline-flex items-center justify-center gap-3 border border-gold/60 text-gold px-7 py-4 font-medium tracking-[0.2em] uppercase text-sm hover:bg-gold/10 transition-all duration-300"
              >
                <MessageCircle size={16} />
                {t.cta_banner.secondary}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONTACT */}
      <ContactSection t={t} lang={lang} />

      {/* FOOTER */}
      <footer className="relative bg-navy-deep overflow-hidden" data-testid="site-footer">
        {/* Top gold hairline */}
        <div className="hairline" />
        {/* Subtle radial gold glow */}
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 w-[700px] h-[300px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center top, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0) 70%)",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-12">
          {/* Hero brand composition — pure gold typography, no PNG */}
          <div className="flex flex-col items-center">
            {/* Top hairline ornament */}
            <div className="flex items-center justify-center w-full max-w-md gap-4 mb-12">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold/50" />
              <span className="text-gold/60 text-xs">✦</span>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold/50" />
            </div>

            {/* Luxury brand mark */}
            <AmaliLogo variant="full" />

            {/* Tagline */}
            <p className="font-serif italic text-cream/70 text-lg sm:text-xl mt-8 max-w-md">
              {t.footer.tagline}
            </p>

            {/* Bottom hairline ornament */}
            <div className="flex items-center justify-center w-full max-w-md gap-4 mt-12">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold/50" />
              <span className="text-gold/60 text-xs">✦</span>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold/50" />
            </div>

            {/* Direct CTA row */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
              <a
                href="https://wa.me/385993509595"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="group inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold/10 px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a
                href="tel:+385993509595"
                data-testid="footer-phone-link"
                className="group inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold/10 px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                <Phone size={14} /> +385 99 350 9595
              </a>
              <a
                href="https://instagram.com/valeria_jacek"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="group inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold/10 px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                @valeria_jacek
              </a>
            </div>
          </div>

          {/* Info row */}
          <div className="mt-16 pt-10 border-t border-gold/15 grid sm:grid-cols-3 gap-8 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-gold/80 mb-3">
                Studio
              </div>
              <div className="text-cream/80">{t.contact.info.name}</div>
              <div className="text-cream/60 mt-1">{t.contact.info.owner}</div>
            </div>
            <div className="sm:text-center">
              <div className="text-[10px] uppercase tracking-[0.35em] text-gold/80 mb-3">
                Lokacija
              </div>
              <div className="text-cream/80">{t.contact.info.location}</div>
              <div className="text-cream/60 mt-1">Dostupna diljem Hrvatske</div>
            </div>
            <div className="sm:text-right">
              <div className="text-[10px] uppercase tracking-[0.35em] text-gold/80 mb-3">
                Kontakt
              </div>
              <div className="text-cream/80">{t.contact.info.phone}</div>
              <div className="text-cream/60 mt-1">{t.contact.info.instagram}</div>
            </div>
          </div>

          {/* Bottom row */}
          <div className="mt-12 pt-6 border-t border-gold/15 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] uppercase tracking-[0.25em] text-cream/50">
            <div>© {new Date().getFullYear()} AMALI · {t.footer.rights}</div>
            <div className="font-serif italic normal-case tracking-normal text-cream/40 text-sm">
              {t.footer.made}
            </div>
          </div>
        </div>
      </footer>

      <FloatingCTA t={t} />
    </div>
  );
};

export default Landing;
